// ─────────────────────────────────────────────────────────────
//  Report-Kennzahlen für die Sales-Pipeline-Karten
//
//  Berechnet aus einem (normalisierten) Report die gleichen Kennzahlen, die
//  auch die Research-/Impact-Seite anzeigt: Fit-Score, Verdict, Kundenwert,
//  Anzahl E-Mail-Texte und Setup-Fortschritt. So zeigen die Container im
//  Board direkt die spannendsten Infos, ohne den Report zu öffnen.
// ─────────────────────────────────────────────────────────────
import { computeProjection, clamp } from './pricingEngine';

// Identisch zur Logik in ResearchPage (verdictForScore), nur kompakter beschriftet.
const verdictForScore = (score) => {
  if (score >= 75) return { label: 'Top-Fit', color: '#4ade80', level: 'high' };
  if (score >= 60) return { label: 'Guter Fit', color: '#4ade80', level: 'high' };
  if (score >= 45) return { label: 'Bedingt', color: '#facc15', level: 'mid' };
  return { label: 'Schwach', color: '#f87171', level: 'low' };
};

export function computeReportMetrics(report) {
  if (!report || typeof report !== 'object') return null;
  const cfg = report.config || {};
  const deal = Number(cfg.revenuePerClose) || 0;
  const margin = Number(cfg.profitMargin) || 0;

  let proj;
  try { proj = computeProjection(cfg); } catch (e) { return null; }

  // Fit-Score (gleiche Formel wie auf der Research-Seite).
  const closeRate = clamp(cfg.closeRate ?? 0.30, 0.01, 1.0);
  const apptsPerYear = proj.expectedAppts * 12;
  const baseCloses = Math.round(apptsPerYear * closeRate);
  const baseProfit = baseCloses * deal * margin;
  const roiMultiple = proj.investmentYear1 > 0 ? baseProfit / proj.investmentYear1 : 0;
  const roiFitScore = clamp(Math.round(40 * (roiMultiple / (roiMultiple + 0.6))), 0, 40);
  const kundenwertScore = clamp(Math.round(30 * (proj.profitPerClose / 6500)), 0, 30);
  const researchRows = Array.isArray(report.researchScores) ? report.researchScores : [];
  const researchSum = researchRows.reduce((s, r) => s + (Number(r.score) || 0), 0);
  const totalScore = clamp(roiFitScore + kundenwertScore + researchSum, 0, 100);

  const emails = Array.isArray(report.emails)
    ? report.emails.filter((e) => e && (e.subject || e.body))
    : [];

  const kickoff = report.kickoff || {};
  const checklist = Array.isArray(kickoff.checklist) ? kickoff.checklist : [];
  const setupTotal = checklist.length;
  const setupDone = checklist.filter((c) => c && c.done).length;

  return {
    totalScore,
    verdict: verdictForScore(totalScore),
    ourMonthly: proj.ourMonthly,
    ourAnnual: proj.ourAnnual,
    revenuePerClose: deal,
    profitPerClose: proj.profitPerClose,
    roiMultiple,
    emailCount: emails.length,
    setupDone,
    setupTotal,
  };
}

// Kompakte EUR-Darstellung für die Karten (z. B. 2.860 € oder 34k €).
export function formatMoneyCompact(n) {
  if (!n || !isFinite(n)) return '–';
  if (n >= 10000) return `${Math.round(n / 1000)}k €`;
  return `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(Math.round(n))} €`;
}
